/**
 * MFA reset panel (GR-3828), rendered on the account Eyes-Only tab.
 *
 * When an account holder loses their authenticator they cannot clear MFA themselves, since the
 * self-service disable needs a working code. This gives system admins the same three writes that
 * were previously run as hand-written SQL: disable the authenticator, disable the recovery codes,
 * clear the account flag. The account holder then signs in with their password and enrols again.
 */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { Button, Message } from 'semantic-ui-react';

import { resetMultiFactor, clearResetMfaRecord } from './reducer';
import { Record } from '../../../modules/bin/utility';

import ConfirmationModal from '../../../components/common/confirmationModal';
import RecordResultMessage from '../../../components/common/recordResult';
import { TemplateHeader } from '../../../components/template/templateHeader';

import { i18nKeys } from '../constants';

interface MfaResetProps {
  accountId: number;
  hasMfaActive: boolean;
  resetMfaRecord: any;
  onReset: (accountId: number) => void;
  onDismissMessage: () => void;
}

const MfaResetView: React.FC<MfaResetProps> = ({
  accountId,
  hasMfaActive,
  resetMfaRecord,
  onReset,
  onDismissMessage
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isResetting = Record.isRecordLoading(resetMfaRecord);

  const onConfirm = () => {
    setConfirmOpen(false);
    onReset(accountId);
  };

  return (
    <div className="mfa-reset" style={{ marginTop: '24px' }}>
      <RecordResultMessage
        record={resetMfaRecord}
        onDismiss={onDismissMessage}
        customSuccessMessage={I18n.t(i18nKeys.MFA_RESET_SUCCESS_MESSAGE)}
      />

      <div className="template-heading">
        <TemplateHeader as="h3" label={i18nKeys.MFA_RESET_HEADER} />
      </div>

      <div style={{ width: '93.75%' }}>
        <Message info>
          <Message.Content>
            {I18n.t(
              hasMfaActive
                ? i18nKeys.MFA_RESET_ACTIVE
                : i18nKeys.MFA_RESET_INACTIVE
            )}
          </Message.Content>
        </Message>

        <Button
          primary
          disabled={!hasMfaActive || isResetting}
          loading={isResetting}
          onClick={() => setConfirmOpen(true)}
        >
          {I18n.t(i18nKeys.MFA_RESET_BUTTON)}
        </Button>
      </div>

      <ConfirmationModal
        open={confirmOpen}
        title={I18n.t(i18nKeys.MFA_RESET_CONFIRM_TITLE)}
        content={I18n.t(i18nKeys.MFA_RESET_CONFIRM_MESSAGE)}
        onClose={() => setConfirmOpen(false)}
        onSubmit={onConfirm}
      />
    </div>
  );
};

const mapState = (state: any) => {
  const account = state.accountSettings.record.data.data;
  return {
    accountId: account.id,
    hasMfaActive: !!account.hasMfaActive,
    resetMfaRecord: state.accountSettings.resetMfaRecord
  };
};

const mapDispatch = (dispatch: any) => {
  return {
    onReset: (accountId: number) => {
      dispatch(resetMultiFactor(accountId));
    },
    onDismissMessage: () => {
      dispatch(clearResetMfaRecord());
    }
  };
};

const MfaReset = connect(mapState, mapDispatch)(MfaResetView);

export default MfaReset;
